/**
 * Calculate the distance between two geographic coordinates using the Haversine formula
 * @param lat1 Latitude of first point
 * @param lon1 Longitude of first point
 * @param lat2 Latitude of second point
 * @param lon2 Longitude of second point
 * @returns Distance in miles
 */
export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 3959; // Earth's radius in miles
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
    Math.cos(toRadians(lat2)) *
    Math.sin(dLon / 2) *
    Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;

  return Math.round(distance * 10) / 10; // Round to 1 decimal place
}

function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

/**
 * Format distance for display
 * @param distance Distance in miles
 * @returns Formatted string (e.g., "2.5 mi" or "< 1 mi")
 */
export function formatDistance(distance: number): string {
  if (distance < 1) {
    return '< 1 mi';
  }
  return `${distance.toFixed(1)} mi`;
}

/**
 * Approximate coordinates for Houston-area cities
 * Used when user hasn't enabled geolocation
 */
export const CITY_COORDINATES: Record<string, { lat: number; lon: number }> = {
  'Houston': { lat: 29.7604, lon: -95.3698 },
  'Sugar Land': { lat: 29.6196, lon: -95.6349 },
  'Katy': { lat: 29.7858, lon: -95.8244 },
  'Pearland': { lat: 29.5636, lon: -95.2861 },
  'The Woodlands': { lat: 30.1658, lon: -95.4613 },
  'Spring': { lat: 30.0799, lon: -95.4172 },
  'Cypress': { lat: 29.9691, lon: -95.6972 },
  'Humble': { lat: 29.9988, lon: -95.2622 },
  'Missouri City': { lat: 29.6185, lon: -95.5377 },
  'League City': { lat: 29.5074, lon: -95.0949 }
};

/**
 * Get coordinates for a city name
 * @param cityName Name of the city
 * @returns Coordinates or null if city not found
 */
export function getCityCoordinates(cityName: string): { lat: number; lon: number } | null {
  return CITY_COORDINATES[cityName] || null;
}
